import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { whenOptions } from "../../config/constants/options";
import { AppDispatch } from "../store";
import { getSessionDocuments } from "../../services/media.services";
import { Complaint, Diagnosis, Prescription, SessionState } from "../../types/consultation";

const initialState: SessionState = {
  formData: {
    weight: "",
    height: "",
    pulse: "",
    temperature: "",
    bp: "",
    complaint: "",
    frequency: "",
    severity: "",
    duration: "",
    date: null,
    clinicalHistory: "",
    additionalNotes: "",
    advice: "",
    test: [],
    unit: "",
    consultFlag: null,
    uuid: "",
    customerId: "",
    markDone: false,
    testInputValue: "",
    primaryPerson:"",
    secondaryPerson:""
  },
  errors: {},
  selectOptions: {
    complaint: [],
    frequency: [],
    severity: [],
    medicine: [],
    dose: [],
    when: whenOptions,
    test: [],
    diagnosis: [],
    primaryPerson:[],
    secondaryPerson:[]
  },
  tables: {
    diagnosis: [{ no: 0, diagnosis: "", duration: "", date: null, unit: "" }],
    prescription: [
      {
        no: 0,
        medicine: "",
        dose: "",
        when: "",
        frequency: "",
        unit_quantity: "",
        notes: "",
        originalNotes: "",
        language: "en",
        unit: "",
      },
    ],
    complaints: [
      {
        complaint: "",
        frequency: "",
        severity: "",
        duration: "",
        unit: "",
        date: null,
      } as Complaint,
    ],
  },
  mainPaymentHistory: [],
  mediaData: {},
  hasUnsavedChanges: false,
};

const consultationSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    setFormData: (
      state,
      action: PayloadAction<Partial<SessionState["formData"]> & { skipUnsavedFlag?: boolean }>
    ) => {
      const { skipUnsavedFlag, ...rest } = action.payload as any;
      state.formData = { ...state.formData, ...rest };
      // Set hasUnsavedChanges to true when form data is modified (unless skipUnsavedFlag is true)
      if (!skipUnsavedFlag) {
        state.hasUnsavedChanges = true;
      }
    },
    setSelectOptions: (
      state,
      action: PayloadAction<Partial<SessionState["selectOptions"]>>
    ) => {
      state.selectOptions = { ...state.selectOptions, ...action.payload };
    },
    setErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload;
    },
    setMediaData: (state, action: PayloadAction<any>) => {
      state.mediaData = action.payload;
    },
    setPaymentHistoryData: (state, action: PayloadAction<any>) => {
      state.mainPaymentHistory = action.payload;
    },
    setTable: (
      state,
      action: PayloadAction<{
        table: "diagnosis" | "prescription" | "complaints";
        rows: Diagnosis[] | Prescription[] | Complaint[];
        skipUnsavedFlag?: boolean;
      }>
    ) => {
      const { table, rows } = action.payload;
      if (table === "diagnosis") {
        state.tables.diagnosis = rows as Diagnosis[];
      } else if (table === "prescription") {
        state.tables.prescription = rows as Prescription[];
      } else if (table === "complaints") {
        state.tables.complaints = rows as Complaint[];
      }
      // Set hasUnsavedChanges to true when table data is modified (unless skipUnsavedFlag is true)
      if (!action.payload.skipUnsavedFlag) {
        state.hasUnsavedChanges = true;
      }
    },
    clearErrors: (state) => {
      state.errors = {};
    },
    clearForm: (state) => {
      state.formData = {
        ...initialState.formData,
        customerId: state.formData.customerId,
      };
      state.errors = {};
      state.tables = {
        diagnosis: [
          { no: 0, diagnosis: "", duration: "", date: null, unit: "" },
        ],
        prescription: [
          {
            no: 0,
            medicine: "",
            dose: "",
            when: "",
            frequency: "",
            unit_quantity: "",
            notes: "",
            originalNotes: "",
            language: "en",
            unit: "",
          },
        ],
        complaints: [
          {
            complaint: "",
            frequency: "",
            severity: "",
            duration: "",
            unit: "",
            date: null,
          } as Complaint,
        ],
      };
      state.mediaData = {};
      state.hasUnsavedChanges = false;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
  },
});

export const {
  setFormData,
  setErrors,
  clearErrors,
  clearForm,
  setSelectOptions,
  setTable,
  setPaymentHistoryData,
  setMediaData,
  setHasUnsavedChanges,
} = consultationSlice.actions;

// Thunk to handle fetching media data by ID
export const getsessionMediaDataById =
  (sessionId: string): any =>
  async (dispatch: AppDispatch, getState: any) => {

    try {
      const res = await getSessionDocuments(sessionId);
      if (res?.data?.status === 200) {
        dispatch(setMediaData(res?.data?.data));
      }
    } catch (err) {
      dispatch(setMediaData({}));
      console.error("Error fetching media data:", err);
    }
  };

export default consultationSlice.reducer;
